import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./RawFruit";
import { FruitRecordService } from "../services/FruitRecordService";
import { MachineService, Machine } from "@services/MachineService";

const FormPage: React.FC = () => {
  const { qrId } = useParams<{ qrId: string }>(); // Fetch qrId from URL
  const [currentData, setCurrentData] = useState<any[]>([]);
  const [formErrors, setFormErrors] = useState({ tss: "", ph: "" });
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false); // Loader for submit button
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
  const [machines, setMachines] = useState<Machine[]>([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await FruitRecordService.fetchData(qrId as string);
      setCurrentData(data.data);
      console.log(data.data);
    } catch (error: any) {
      setError("Failed to fetch data");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Fetch all farmers & machines
  const fetchMachines = async () => {
    try {
      const machinesRes = await MachineService.fetchMachines()
      setMachines(machinesRes.data);
    } catch (err) {
      console.error("Error fetching farmers/machines:", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true); // Start loader
    const formData = new FormData(e.currentTarget);
    const tss = Number(formData.get("tss"));
    const ph = Number(formData.get("ph"));

    const errors = { tss: "", ph: "" };
    if (tss < 1 || tss > 25) errors.tss = "TSS must be between 1 and 25.";
    if (ph < 1 || ph > 14) errors.ph = "pH must be between 1 and 14.";

    setFormErrors(errors);

    if (errors.tss || errors.ph) {
      setSubmitting(false); // Stop loader if there are errors
      return;
    }

    try {
      const data = {
        machineId: formData.get("machineId") as string,
        weight: parseFloat(formData.get("weight") as string),
        tss: parseFloat(formData.get("tss") as string),
        ph: parseFloat(formData.get("ph") as string),
        remarks: formData.get("remarks") as string,
        qrId: qrId as string,
        image: formData.get("image") as File,
      };
      await FruitRecordService.submitData(data);
    //   e.currentTarget.reset();
      
    } catch (error) {
        console.log(error)
      console.error("Error submitting data:", error);
    } finally {
      setIsFormVisible(false);
      setSubmitting(false); // Stop loader after submission
      fetchData();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    navigate("/login");
  };

  useEffect(() => {
    fetchMachines();
      fetchData();
  }, []);

  return (
    <div className="page">
      <div className="container">
        <div className="header">
          {isAuthenticated && (
            <>
              <button
                className="button"
                onClick={() => setIsFormVisible(true)}
                style={{ position: "absolute", top: "10px" }}
              >
                Add Data
              </button>
            </>
          )}
        </div>

        <h1 className="title">QR Code Data</h1>

        {loading && <p>Loading data...</p>}
        {error && <p>{error}</p>}

        {isFormVisible && isAuthenticated && (
          <div className="form-popup">
            <form onSubmit={handleSubmit} className="form">
            <label className="label">Machine:</label>
              <select name="machineId" className="input">
                <option value="">-- Select Machine --</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.owner} (ID: {m.id})
                  </option>
                ))}
              </select>

              <label className="label">Weight (kg):</label>
              <input
                name="weight"
                type="number"
                step="0.01"
                min="1"
                max="250"
                className="input"
              />

              <label className="label">TSS (1 - 25):</label>
              <input
                name="tss"
                type="number"
                step="0.01"
                min="1"
                max="25"
                className="input"
              />
              {formErrors.tss && (
                <div className="error">{formErrors.tss}</div>
              )}

              <label className="label">pH (1 - 14):</label>
              <input
                name="ph"
                type="number"
                step="0.01"
                min="1"
                max="14"
                className="input"
              />
              {formErrors.ph && <div className="error">{formErrors.ph}</div>}

              <label className="label">Remarks:</label>
              <textarea name="remarks" className="textarea" />

              <label className="label">QR ID:</label>
              <textarea name="qrId" value={qrId} disabled className="textarea" />

              <label className="label">Upload Image:</label>
              <input name="image" type="file" className="input" />

              <button type="submit" className="button" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="loader"></span> Submitting...
                  </>
                ) : (
                  "Submit Data"
                )}
              </button>
              <button
                type="button"
                className="button cancel"
                onClick={() => setIsFormVisible(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {currentData.length > 0 ? (
          <>
            <h2 className="sub-title">Current Data</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>Fruit</th>
                  <th>Weight</th>
                  <th>TSS</th>
                  <th>pH</th>
                  <th>Remarks</th>
                  <th>Timestamp</th>
                  <th>Image</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((record, index) => (
                  <tr key={index}>
                    <td>{record.fruit}</td>
                    <td>{record.weight}</td>
                    <td>{record.tss}</td>
                    <td>{record.ph}</td>
                    <td>{record.remarks}</td>
                    <td>{record.createdAt}</td>
                    <td>
                      {record.image ? (
                        <img
                          src={record.image}
                          alt="Uploaded"
                          className="thumbnail"
                        />
                      ) : (
                        "N/A"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <p className="no-data-message">No current data available.</p>
        )}
      </div>
    </div>
  );
};

export default FormPage;
